import React, { useEffect, useRef, useState } from 'react';
import { ImageIcon, Loader2, Upload } from 'lucide-react';
import Compressor from 'compressorjs';
import { useAuth } from '@soap/modules';
import { Button } from '../../ui/button';
import { RegenerateFigureDialog } from '../dialogs/RegenerateFigureDialog';
import { FigureGenerationFailureActions } from './FigureGenerationFailureActions';
import { getPrimaryFunctionAppRoot } from '../../utils/functionAppRoot';
import type { InstructionStepDto, StepRasterFigureDto } from './types';
import {
    formatRasterFigureLabel,
    getRasterGenerationPromptMeta,
    isAiGeneratedRasterFigure,
    isRasterFigureAwaitingImage,
    isRasterFigureFailed,
    isRasterFigureGenerating,
    isRasterFigureReady,
    isTopicImageFigure,
    canUploadRasterFigure,
    remainingRasterGenerationMs,
    RASTER_CHECK_STATUS_HINT,
    RASTER_GENERATING_MESSAGE,
    RASTER_GENERATION_TIMED_OUT_MESSAGE,
    RASTER_STILL_GENERATING_AFTER_CHECK_MESSAGE,
    resolveSelectedRasterOption,
    type MediaReviewSlotKind,
} from './mediaReviewUtils';
import { formatStepBadgeFromIds } from './instructionStepUtils';
import { RasterImagePreview } from './RasterImagePreview';
import { useSessionDebugMode } from '../../hooks/useSessionDebugMode';

type RasterFigureReviewCardProps = {
    stepId: string;
    figure: StepRasterFigureDto;
    instructionSteps?: InstructionStepDto[];
    videoReviewMode?: boolean;
    /** When true, card sits inside worksheet/reading text (no separate Images group). */
    inline?: boolean;
    isRasterApproved?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
    isRasterApproving?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
    onApproveRaster?: (stepId: string | undefined, figureKey: string | undefined) => void;
    onRegenerateRaster?: (
        stepId: string | undefined,
        figureKey: string | undefined,
        revisionNotes?: string
    ) => void;
    isRasterRegenerating?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
    onAcceptTextOnly?: (stepId: string | undefined, figureKey: string | undefined) => void;
    isAcceptingTextOnly?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
    hostMaterialKind?: 'worksheet' | 'reading';
    onOpenRegenerateHostMaterial?: () => void;
    onOpenRegenerateLesson?: () => void;
    onOpenRegenerateUnit?: () => void;
    onUploadRaster?: (stepId: string | undefined, figureKey: string | undefined, blobId: string, title?: string) => void;
    isRasterUploading?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
    onCheckRasterStatus?: (stepId: string | undefined, figureKey: string | undefined) => void;
    isRasterStatusChecking?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
    isRasterStillGeneratingAfterCheck?: (
        stepId: string | undefined,
        figureKey: string | undefined
    ) => boolean;
    buildMediaSlotDomId?: (
        kind: MediaReviewSlotKind,
        materialId: string | undefined,
        figureKey?: string
    ) => string | undefined;
};

export function RasterFigureReviewCard({
    stepId,
    figure,
    instructionSteps,
    videoReviewMode = false,
    inline = false,
    isRasterApproved,
    isRasterApproving,
    onApproveRaster,
    onRegenerateRaster,
    isRasterRegenerating,
    onAcceptTextOnly,
    isAcceptingTextOnly,
    hostMaterialKind,
    onOpenRegenerateHostMaterial,
    onOpenRegenerateLesson,
    onUploadRaster,
    isRasterUploading,
    onCheckRasterStatus,
    isRasterStatusChecking,
    isRasterStillGeneratingAfterCheck,
    buildMediaSlotDomId,
}: RasterFigureReviewCardProps) {
    const sessionDebugMode = useSessionDebugMode();
    const generationClock = useRasterGenerationClock(figure);
    const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
    const figureKey = figure.e251_FigureKey;
    const approved = isRasterApproved?.(stepId, figureKey) ?? figure.e251_RasterApproved === true;
    const approving = isRasterApproving?.(stepId, figureKey) ?? false;
    const regenerating = isRasterRegenerating?.(stepId, figureKey) ?? false;
    const acceptingTextOnly = isAcceptingTextOnly?.(stepId, figureKey) ?? false;
    const uploading = isRasterUploading?.(stepId, figureKey) ?? false;
    const statusChecking = isRasterStatusChecking?.(stepId, figureKey) ?? false;
    const stillGeneratingAfterCheck =
        isRasterStillGeneratingAfterCheck?.(stepId, figureKey) ?? false;
    const showApproval = videoReviewMode;
    const topicImage = isTopicImageFigure(figure);
    const awaitingImage = isRasterFigureAwaitingImage(figure);
    const generating = isRasterFigureGenerating(figure, generationClock);
    const failed = isRasterFigureFailed(figure, generationClock) && (figure.e251_RasterOptions?.length ?? 0) === 0;
    const ready = isRasterFigureReady(figure, generationClock);
    const needsApproval = showApproval && ready && !approved && !approving;
    const domId = buildMediaSlotDomId?.('raster', stepId, figureKey);
    const highlightNeedsAttention =
        needsApproval || failed || (showApproval && awaitingImage && !approved);
    const cardClassName = [
        inline ? 'rounded-md border border-gray-200 px-3' : '',
        highlightNeedsAttention ? 'bg-[#D4616B]/5 py-2.5' : 'py-2.5',
    ]
        .filter(Boolean)
        .join(' ');
    const stepBadge = formatStepBadgeFromIds([stepId], instructionSteps);
    const currentOption = resolveSelectedRasterOption(figure) ?? null;
    const imageTitle =
        currentOption?.e251_RasterOptionTitle || figure.e251_RasterTitle || formatRasterFigureLabel(figure);
    const imageUrl = currentOption?.e251_ImageUrl || figure.e251_ImageUrl;
    const imageBlobId = currentOption?.e251_BlobId;
    const showAiWatermark = isAiGeneratedRasterFigure(figure);
    const allowUpload =
        Boolean(onUploadRaster) && canUploadRasterFigure(figure, instructionSteps, stepId);
    const generationPrompt = getRasterGenerationPromptMeta(figure, instructionSteps, stepId);

    const generationPromptDetails =
        sessionDebugMode && generationPrompt ? (
            <details className="mt-2 text-xs text-gray-600">
                <summary className="cursor-pointer select-none text-gray-500 hover:text-gray-700">
                    Raster generation prompt
                </summary>
                <dl className="mt-1 space-y-1 rounded border border-gray-100 bg-gray-50 p-2 text-gray-700">
                    {generationPrompt.imageProvider ? (
                        <div>
                            <dt className="font-medium text-gray-500">Provider</dt>
                            <dd>{generationPrompt.imageProvider}</dd>
                        </div>
                    ) : null}
                    {generationPrompt.imageModel ? (
                        <div>
                            <dt className="font-medium text-gray-500">Model</dt>
                            <dd>{generationPrompt.imageModel}</dd>
                        </div>
                    ) : null}
                    {generationPrompt.description ? (
                        <div>
                            <dt className="font-medium text-gray-500">Focus sent to model</dt>
                            <dd className="whitespace-pre-wrap">{generationPrompt.description}</dd>
                        </div>
                    ) : null}
                    {generationPrompt.figureType ? (
                        <div>
                            <dt className="font-medium text-gray-500">
                                {topicImage ? 'Image type' : 'Diagram type'}
                            </dt>
                            <dd>{generationPrompt.figureType}</dd>
                        </div>
                    ) : null}
                    {generationPrompt.figureRole ? (
                        <div>
                            <dt className="font-medium text-gray-500">Figure role</dt>
                            <dd>{generationPrompt.figureRole}</dd>
                        </div>
                    ) : null}
                    {generationPrompt.imageGenerationPrompt ? (
                        <div>
                            <dt className="font-medium text-gray-500">Extracted brief</dt>
                            <dd className="whitespace-pre-wrap">
                                {generationPrompt.imageGenerationPrompt}
                            </dd>
                        </div>
                    ) : null}
                </dl>
            </details>
        ) : null;

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [localUploading, setLocalUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const { idToken, authReady } = useAuth('raster-figure-upload');

    const handleUploadClick = () => fileInputRef.current?.click();

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !figureKey) return;
        setUploadError(null);
        setLocalUploading(true);
        try {
            const compressedBlob = await new Promise<Blob>((resolve, reject) => {
                new Compressor(file, {
                    checkOrientation: false,
                    maxWidth: 1600,
                    maxHeight: 1200,
                    success: resolve,
                    error: reject,
                });
            });
            if (!authReady) throw new Error('Authentication not ready');
            const blobId = crypto.randomUUID();
            const endpoint = `${getPrimaryFunctionAppRoot()}/AddBlob`;
            const response = await fetch(`${endpoint}?id=${blobId}&it=${idToken}`, {
                method: 'POST',
                body: compressedBlob,
            });
            if (!response.ok) throw new Error('Upload failed');
            onUploadRaster?.(stepId, figureKey, blobId, file.name);
        } catch {
            setUploadError('Upload failed. Please try again.');
        } finally {
            setLocalUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <div id={domId} className={cardClassName}>
            <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-2">
                    <ImageIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#1a5172]" aria-hidden />
                    <div className="min-w-0">
                        <h6 className="text-sm font-semibold text-gray-900">
                            {formatRasterFigureLabel(figure)}
                            {topicImage ? (
                                <span className="ml-2 text-xs font-normal text-gray-500">Topic image</span>
                            ) : null}
                        </h6>
                        {stepBadge ? <p className="mt-0.5 text-xs text-gray-500">{stepBadge}</p> : null}
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    {generating ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
                            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                            Up to 5 mins
                        </span>
                    ) : null}
                    {awaitingImage && !approved ? (
                        <span className="inline-flex items-center rounded-full bg-[#D4616B]/10 px-2 py-0.5 text-xs font-semibold text-[#BE5059]">
                            Needs image
                        </span>
                    ) : null}
                    {failed ? (
                        <span className="inline-flex items-center rounded-full bg-[#D4616B]/10 px-2 py-0.5 text-xs font-semibold text-[#BE5059]">
                            Failed
                        </span>
                    ) : null}
                    {needsApproval ? (
                        <span className="inline-flex items-center rounded-full bg-[#D4616B]/10 px-2 py-0.5 text-xs font-semibold text-[#BE5059]">
                            Needs approval
                        </span>
                    ) : null}
                    {showApproval && approved ? (
                        <span className="inline-flex items-center rounded-full bg-[#f0fdf4] px-2 py-0.5 text-xs font-semibold text-[#15803d]">
                            Approved
                        </span>
                    ) : null}
                </div>
            </div>

            {awaitingImage && !imageUrl ? (
                <div className="space-y-2">
                    <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-600">
                        <p>Upload a photograph for this topic image, or regenerate to create one with AI.</p>
                    </div>
                    {generationPromptDetails}
                </div>
            ) : generating && !imageUrl ? (
                <div className="space-y-2">
                    <div
                        className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center text-sm text-gray-600"
                        role="status"
                        aria-live="polite"
                    >
                        <Loader2 className="h-8 w-8 animate-spin text-[#1a5172]" aria-hidden />
                        <p>{RASTER_GENERATING_MESSAGE}</p>
                        {onCheckRasterStatus ? (
                            <p className="text-xs text-gray-500">{RASTER_CHECK_STATUS_HINT}</p>
                        ) : null}
                        {stillGeneratingAfterCheck ? (
                            <p className="text-xs text-gray-600">
                                {RASTER_STILL_GENERATING_AFTER_CHECK_MESSAGE}
                            </p>
                        ) : null}
                        {onCheckRasterStatus ? (
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-8 text-xs"
                                disabled={statusChecking || regenerating}
                                onClick={() => onCheckRasterStatus(stepId, figureKey)}
                            >
                                {statusChecking ? (
                                    <>
                                        <Loader2
                                            className="mr-1.5 h-3.5 w-3.5 animate-spin"
                                            aria-hidden
                                        />
                                        Checking…
                                    </>
                                ) : (
                                    'Check status'
                                )}
                            </Button>
                        ) : null}
                    </div>
                    {generationPromptDetails}
                </div>
            ) : imageUrl ? (
                <div className="space-y-2 bg-white p-3">
                    {imageTitle ? <p className="text-sm font-medium text-gray-900">{imageTitle}</p> : null}
                    <RasterImagePreview
                        src={imageUrl}
                        blobId={imageBlobId}
                        alt={imageTitle}
                        title={imageTitle}
                        showAiWatermark={showAiWatermark}
                    />
                    {generationPromptDetails}
                </div>
            ) : failed ? (
                <div className="space-y-3 rounded-md border border-[#D4616B]/30 bg-white p-3 text-sm text-[#BE5059]">
                    <p className="font-medium text-gray-900">We were unable to generate this image.</p>
                    {figure.e251_FigurePurpose ? (
                        <p className="text-gray-800">
                            <span className="font-semibold">Purpose:</span> {figure.e251_FigurePurpose}
                        </p>
                    ) : null}
                    {figure.e251_GenerationError &&
                    !figure.e251_GenerationError.includes('Purpose:') ? (
                        <p className="text-gray-700">{figure.e251_GenerationError}</p>
                    ) : null}
                    <FigureGenerationFailureActions
                        hostMaterialKind={hostMaterialKind}
                        onOpenRegenerateHostMaterial={onOpenRegenerateHostMaterial}
                        onOpenRegenerateLesson={onOpenRegenerateLesson}
                    />
                    {generationPromptDetails}
                </div>
            ) : generationPromptDetails ? (
                <div className="space-y-2 bg-white p-3">{generationPromptDetails}</div>
            ) : null}

            {showApproval ? (
                <div className="flex flex-wrap items-center gap-2 pt-3">
                    {allowUpload ? (
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".jpg,.jpeg,.jfif,.png,.webp"
                            className="hidden"
                            onChange={handleFileSelect}
                        />
                    ) : null}
                    {approving ? (
                        <span className="inline-flex items-center gap-2 text-xs text-gray-600">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                            Saving your image approval…
                        </span>
                    ) : regenerating || uploading || localUploading ? (
                        <span className="inline-flex items-center gap-2 text-xs text-gray-600">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                            {uploading || localUploading ? 'Uploading image…' : 'Regenerating image…'}
                        </span>
                    ) : (
                        <>
                            {ready && needsApproval ? (
                                <Button
                                    type="button"
                                    size="sm"
                                    className="h-8 gap-1 bg-[#16a34a] text-xs text-white hover:bg-[#15803d]"
                                    onClick={() => onApproveRaster?.(stepId, figureKey)}
                                >
                                    Approve image
                                </Button>
                            ) : null}
                            {onRegenerateRaster ? (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="h-8 text-xs"
                                    onClick={() => setRegenerateDialogOpen(true)}
                                >
                                    Regenerate Image
                                </Button>
                            ) : null}
                            {allowUpload ? (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="h-8 gap-1 text-xs"
                                    onClick={handleUploadClick}
                                >
                                    <Upload className="h-3.5 w-3.5" aria-hidden />
                                    Upload
                                </Button>
                            ) : null}
                            {uploadError ? (
                                <span className="text-xs text-[#BE5059]">{uploadError}</span>
                            ) : null}
                            {awaitingImage && !approved ? (
                                <span className="text-xs text-[#BE5059]">
                                    Upload a photograph or regenerate, then approve.
                                </span>
                            ) : needsApproval ? (
                                <span className="text-xs text-[#BE5059]">
                                    Review the generated image, then approve to confirm your choice.
                                </span>
                            ) : approved ? (
                                <span className="text-xs text-gray-500">
                                    {imageTitle
                                        ? `Image confirmed: ${imageTitle}`
                                        : 'Image confirmed for this lesson.'}
                                </span>
                            ) : null}
                        </>
                    )}
                </div>
            ) : null}
            {regenerateDialogOpen && figureKey && onRegenerateRaster ? (
                <RegenerateFigureDialog
                    figureKey={figureKey}
                    onClose={() => setRegenerateDialogOpen(false)}
                    onConfirm={notes => {
                        onRegenerateRaster(stepId, figureKey, notes || undefined);
                    }}
                />
            ) : null}
        </div>
    );
}

function useRasterGenerationClock(figure: StepRasterFigureDto): number {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const remaining = remainingRasterGenerationMs(figure, Date.now());
        if (remaining == null) return;
        if (remaining <= 0) {
            setNow(Date.now());
            return;
        }
        const id = window.setTimeout(() => setNow(Date.now()), remaining);
        return () => window.clearTimeout(id);
    }, [figure.e251_GenerationState, figure.e251_GenerationStartedAt]);

    return now;
}
